import { InquiryModalProvider } from '@/components/inquiry/InquiryModal';
import CookieConsent from '@/components/tracking/CookieConsent';
import TrackingScripts from '@/components/tracking/TrackingScripts';
import { InterpolationProvider } from '@/contexts/InterpolationContext';
import { TrackingProvider } from '@/contexts/TrackingContext';
import PublicHeaderFooterLayout from '@/layouts/public/public-header-footer-layout';
import { announceAmplitudeHead, type AmplitudePageProps } from '@/lib/amplitude';
import { initAnalytics } from '@/lib/analytics-tracker';
import { Head, usePage } from '@inertiajs/react';
import { useEffect, useId, useLayoutEffect, type PropsWithChildren } from 'react';

const pageKeys = new WeakMap<object, number>();
let nextPageKey = 0;
function PublicAnalyticsHead() {
    const page = usePage();
    if (!pageKeys.has(page)) pageKeys.set(page, ++nextPageKey);
    return <PublicAnalyticsHeadMarker key={pageKeys.get(page)} url={page.url} props={page.props as AmplitudePageProps} />;
}
function PublicAnalyticsHeadMarker({ url, props }: { url: string; props: AmplitudePageProps }) {
    const token = useId();
    useLayoutEffect(() => announceAmplitudeHead(token, url, props), [token, url, props]);
    return (
        <Head>
            <meta name="lrt-amplitude-view" head-key="lrt-amplitude-view" content={token} />
        </Head>
    );
}

interface PublicLayoutProps extends PropsWithChildren {
    headerConfig?: Record<string, unknown>;
}

export default function PublicLayout({ children, headerConfig }: PublicLayoutProps) {
    // initAnalytics returns its teardown; PublicLayout is non-persistent, so
    // every Inertia navigation unmounts it — without the cleanup each page
    // left its listeners behind and one tab-hide fired N time events.
    useEffect(() => initAnalytics(), []);

    return (
        <InterpolationProvider>
            <PublicAnalyticsHead />
            <TrackingProvider>
                <InquiryModalProvider>
                    <PublicHeaderFooterLayout headerConfig={headerConfig}>{children}</PublicHeaderFooterLayout>
                </InquiryModalProvider>
                <TrackingScripts />
                <CookieConsent />
            </TrackingProvider>
        </InterpolationProvider>
    );
}
